import {
  Grid,
  IconButton,
  Paper,
  TextField as MUTextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import { Alert } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { get, has } from 'lodash';
import { FC, useMemo } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Error,
  FieldProps,
  InputProps,
  Loading,
  required,
  SelectArrayInputProps,
  SimpleFormIterator,
  useGetList,
  useInput,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { useFormState } from 'react-final-form';
import { Link } from 'react-router-dom';
import { isPspAdministrator } from '../../configuration';
import { Feature, Subscription } from '../../types';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    padding: theme.spacing(2, 2),
  },
}));

/**
 * The tab with company subscriptions.
 *
 * Only visible for administrators and in read-only mode.
 */
const SubscriptionsTab: FC<FieldProps<Subscription>> = () => {
  const classes = useStyles();
  const translate = useTranslate();
  const { loaded, permissions } = usePermissions();

  // Fetch all the features exposed by the Doc Process platform.
  const { data, loading, error } = useGetList<Feature>(
    'configuration-dxfeatures',
    {
      page: 1,
      perPage: 200, // Should not be more than 200 before a long time.
    }
  );

  // Get the current form values to compute addedFeatureIds below.
  const { values } = useFormState({ subscription: { values: true } });

  if (error) return <Error error={error} />;
  if (!loaded || loading) return <Loading />;

  // 'data' is a JSON object whose properties are the feature ID.
  // Convert it into an array of features and add a title to each
  // taking the i18n translation of their ID if any.
  const features = Object.values(data)?.map((f) => ({
    ...f,
    title: translate(`resources.dxfeatures.${f.id}.description`, {
      _: f.id,
    }),
  }));

  // Get the IDs of the features already added into the form.
  const addedFeatureIds =
    values?.subscriptions
      // When just added in the react-admin ArrayInput (not selected yet
      // in the dropdown list), values.subscriptions contains an undefined
      // element, so skip it.
      ?.filter((s) => !!s?.feature)
      .map((s) => s.feature.id) || [];

  const subtitle = () => {
    return (
      <Typography variant='caption'>
        {!isPspAdministrator(permissions) && (
          <>
            {translate(
              'resources.companies.edit.tabs.subscriptions.subtitle_1',
              {
                _:
                  'All the Doc Process features (read-only) the company subscribed to. ' +
                  'They depend on the contract signed with Doc Process. Contact the ',
              }
            )}
            <a href='mailto:support@doc-process.com'>
              {translate(
                'resources.companies.edit.tabs.subscriptions.subtitle_2',
                { _: 'support' }
              )}
            </a>
            {translate(
              'resources.companies.edit.tabs.subscriptions.subtitle_3',
              { _: ' if you want to change this list.' }
            )}
            <br />
          </>
        )}
        {isPspAdministrator(permissions) && (
          <Alert severity='warning' style={{ margin: '1em' }}>
            {translate(
              'resources.companies.edit.tabs.subscriptions.psp_message',
              {
                _:
                  'For each feature, a set of excluded roles can be selected in order ' +
                  'to limit the list of available roles allowed for any subscribed ' +
                  'feature when assigning the user account usages',
              }
            )}
          </Alert>
        )}
        {translate('resources.companies.edit.tabs.subscriptions.subtitle_4', {
          _: 'Once subscribed, each feature usage can be assigned to a user account through the Accounts menu',
        })}
        <IconButton
          color='primary'
          component={Link}
          to={'/configuration-accounts'}
          size='small'
        >
          <EditIcon style={{ width: '16px', height: '16px' }} />
        </IconButton>
        .
      </Typography>
    );
  };

  return (
    <Paper className={classes.paper} style={{ marginTop: '1em' }}>
      {subtitle()}
      <ArrayInput label='' source='subscriptions'>
        <SimpleFormIterator
          disableAdd={
            !isPspAdministrator(permissions) ||
            addedFeatureIds.length >= features.length
          }
          disableRemove={!isPspAdministrator(permissions)}
        >
          <SubscriptionInput
            features={features}
            addedFeatureIds={addedFeatureIds}
            disabled={!isPspAdministrator(permissions)}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </Paper>
  );
};

const SubscriptionInput: FC<
  Omit<InputProps, 'source'> & {
    source?: string;
  }
> = ({ source, features, addedFeatureIds, disabled }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const featureId = get(values, `${source}.feature.id`);
  const roleIds =
    features.find((f) => f.id === featureId)?.availableRoles.map((r) => r.id) ||
    [];
  const addedExcludedRoleIds =
    values?.subscriptions
      ?.filter((s) => !!s?.feature) // skip potential new added subscription not yet assigned
      ?.filter((s) => s.feature.id === featureId)
      ?.filter((s) => !!s?.excludedRoleIds) // skip potential new added subscription not yet assigned
      .map((s) =>
        s.excludedRoleIds.map((roleId) => ({
          id: roleId.id ? roleId.id : roleId,
          // Look for the role description into the i18n domain bundles (i18n/xx-domain.tsx),
          // key: resources.dxfeatures.<featureId>.<roleId>, and default to the roleId itself.
          name: translate(
            `resources.dxfeatures.${featureId}.${
              roleId.id ? roleId.id : roleId
            }`,
            {
              _: roleId.id ? roleId.id : roleId,
            }
          ),
        }))
      )?.[0] || [];

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={2}>
        <FeatureInput
          source={`${source}.feature`}
          features={features}
          addedFeatureIds={addedFeatureIds}
          disabled={disabled}
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid item xs={8}>
        {/* If a feature is selected, display a select box with its excluded roles. */}
        {isPspAdministrator(permissions) &&
          has(values, `${source}.feature.id`) && (
            <ExcludedRoleInput
              source={`${source}.excludedRoleIds`}
              addedExcludedRoleIds={addedExcludedRoleIds}
              featureId={featureId}
              roleIds={roleIds}
            />
          )}
      </Grid>
      <Grid item xs={2}>
        <BooleanInput
          label='resources.companies.edit.tabs.subscriptions.active'
          source={`${source}.active`}
          disabled={disabled}
          defaultValue={true}
        />
      </Grid>
    </Grid>
  );
};

const FeatureInput: FC<InputProps> = (props) => {
  // Link the material-ui field below (Autocomplete )with the react-admin input framework.
  const {
    input: { name, onChange, ...rest },
    meta: { touched, error },
    isRequired,
  } = useInput(props);
  const translate = useTranslate();
  const { disabled, features, addedFeatureIds } = props;

  return (
    <Autocomplete
      options={features
        // Remove the already added features.
        .filter((f) => !addedFeatureIds.includes(f.id))
        // Sort them to get a nicer selection dropdown list.
        .sort((f1, f2) => f1.title.localeCompare(f2.title))}
      onChange={(event, val) => onChange(val)}
      getOptionLabel={(feature) =>
        !!feature?.id
          ? translate(`resources.dxfeatures.${feature.id}.description`, {
              _: feature.id, // Default to the feature ID.
            })
          : ''
      }
      renderInput={(params) => (
        <MUTextField
          {...params}
          name={name}
          label={
            !disabled
              ? translate('resources.companies.edit.tabs.subscriptions.feature')
              : ''
          }
          variant='standard'
          helperText={touched && error && translate(error)}
          error={!!(touched && error)}
          InputProps={{
            ...params.InputProps,
            // Remove the input box underline, not nice when in an react-admin
            // ArrayInput.
            disableUnderline: true,
          }}
          required={isRequired}
        />
      )}
      {...rest}
      disabled={disabled}
    />
  );
};

const ExcludedRoleInput: FC<
  SelectArrayInputProps & { featureId: string; roleIds: Array<string> }
> = (props: any) => {
  const translate = useTranslate();
  const {
    input: { name, onChange, value, ...rest },
    meta: { touched, error },
    isRequired,
  } = useInput(props);

  const { featureId, roleIds, addedExcludedRoleIds } = props;

  const options = roleIds.map((roleId) => ({
    id: roleId,
    // Look for the role description into the i18n domain bundles (i18n/xx-domain.tsx),
    // key: resources.dxfeatures.<featureId>.<roleId>, and default to the roleId itself.
    name: translate(`resources.dxfeatures.${featureId}.${roleId}`, {
      _: roleId,
    }),
  }));

  const firstLetter = (option) => {
    const firstLetter = (option.name[0] || '').toUpperCase();
    return /[0-9]/.test(firstLetter) ? '0-9' : firstLetter;
  };

  const renderInput = (params) => (
    <MUTextField
      {...params}
      name={name}
      variant='standard'
      label={translate(
        'resources.companies.edit.tabs.subscriptions.excludedRoleIds'
      )}
      error={!!(touched && error)}
      helperText={touched && error}
      required={isRequired}
      InputProps={{ ...params.InputProps, disableUnderline: true }}
    />
  );

  const formattedValue = useMemo(() => {
    if (!value) return [];
    return value.map((v) =>
      v.id
        ? { id: v.id, name: v.name }
        : {
            id: v,
            name: translate(`resources.dxfeatures.${featureId}.${v}`, {
              _: v,
            }),
          }
    );
  }, [featureId, translate, value]);

  const handleOnChange = (val) => {
    onChange(val);
  };

  // When less than 5 roles, display a simple dropdown list, otherwise a list with grouped
  // entry per first letter.
  if (options.length < 6) {
    return (
      <Autocomplete
        {...rest}
        value={formattedValue}
        multiple
        size='small'
        options={options
          .filter((r) => !addedExcludedRoleIds.map((x) => x.id).includes(r.id))
          .sort((a, b) => {
            const order = -firstLetter(b).localeCompare(firstLetter(a));
            return order !== 0 ? order : -b.name.localeCompare(a.name);
          })}
        getOptionSelected={(option, value) => {
          return option.id === value.id || option.id === value;
        }}
        onChange={(event, val) => handleOnChange(val)}
        getOptionLabel={(role) => {
          return role.name;
        }}
        renderInput={renderInput}
      />
    );
  } else {
    return (
      <Autocomplete
        {...rest}
        value={formattedValue}
        multiple
        size='small'
        options={options
          .filter((r) => !addedExcludedRoleIds.map((x) => x.id).includes(r.id))
          .sort((a, b) => {
            const order = -firstLetter(b).localeCompare(firstLetter(a));
            return order !== 0 ? order : -b.name.localeCompare(a.name);
          })}
        groupBy={(option) => firstLetter(option)}
        getOptionSelected={(option, value) => {
          return option.id === value.id || option.id === value;
        }}
        onChange={(event, val) => handleOnChange(val)}
        getOptionLabel={(role) => {
          return role.name;
        }}
        renderInput={renderInput}
      />
    );
  }
};

export default SubscriptionsTab;
